import { useGetPatientDetails } from '@/hooks/patients/useGetPatientDetails';
import { Box, Grid, Typography } from '@mui/material';
import { useContext, useMemo, useState } from 'react';
import { AddressForm } from '../../forms/AddressForm/AddressForm';
import { IhiInformation } from '../../forms/IhiInformation/IhiInformation';
import { Medicare } from '../../forms/Medicare/Medicare';
import { PatientInformation } from '../../forms/PatientInformation/PatientInformation';
import { parseStreetAddress } from '../../hooks/useParseAddress';
import { PatientContext } from '../../PatientContext';
import { PATIENT_DETAIL_ENDPOINTS } from './utils/constants';

export const PatientCheck = () => {
  const { patientId } = useContext(PatientContext);
  const [consentFormSent, setConsentFormSent] = useState(false);

  const { result, isError, isLoading } = useGetPatientDetails(patientId, PATIENT_DETAIL_ENDPOINTS);

  const patientData = result?.data?.body.data;
  const basicInfo = patientData?.basicInfo?.attributes;
  const identificationInfo = patientData?.identificationInfo?.attributes;
  const residentialAddress = patientData?.basicInfo?.attributes?.residentialAddress;
  const contactDetails = patientData?.contactDetails?.attributes;

  const showConsentFormResendLink =
    !consentFormSent && basicInfo && contactDetails ? !basicInfo.consent && Boolean(contactDetails.email) : false;

  const defaultValues = useMemo(() => {
    const parsedAddress = parseStreetAddress(residentialAddress?.address);

    return {
      patient: {
        firstName: basicInfo?.firstName ?? '',
        lastName: basicInfo?.lastName ?? '',
        dob: basicInfo?.dob ?? '',
        consent: Boolean(basicInfo?.consent),
        gender: basicInfo?.gender ?? '',
        atsiStatus: basicInfo?.atsiStatus ?? null,
        doctorId: basicInfo?.doctorId ?? null,
        dvaNumber: identificationInfo?.dvaNumber ?? ''
      },
      medicare: {
        medicareNumber: identificationInfo?.medicareNumber ?? '',
        medicareExpDate: identificationInfo?.medicareExpDate ?? '',
        medicareRefNumber: identificationInfo?.medicareRefNumber ?? ''
      },
      ihi: {
        ihiNumber: identificationInfo?.ihiNumber,
        ihiNumberStatus: identificationInfo?.ihiNumberStatus,
        ihiRecordStatus: identificationInfo?.ihiRecordStatus
      },
      address: {
        streetLine1: parsedAddress.line1 ?? '',
        streetLine2: parsedAddress.line2 ?? '',
        city: residentialAddress?.city ?? '',
        zipCode: residentialAddress?.zipCode ?? '',
        state: residentialAddress?.state?.id ?? 0
      }
    };
  }, [basicInfo, identificationInfo, residentialAddress]);

  const handleResendConsent = (data: { showConsentFormResendLink: boolean }) => {
    // If showConsentFormResendLink is false, that means consent form was sent
    setConsentFormSent(!data.showConsentFormResendLink);
  };

  if (!isLoading && (isError || !patientData)) {
    return (
      <Box>
        <Typography color="error" align="center" variant="body1">
          Error loading patient data.
        </Typography>
      </Box>
    );
  }

  const isIhiActive = identificationInfo?.ihiNumberStatus?.toLowerCase() === 'active';
  const formProps = { editable: false, isLoading, isIhiActive };

  return (
    <Box>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <PatientInformation
            showConsentFormResendLink={showConsentFormResendLink}
            onResendConsent={handleResendConsent}
            defaultValues={defaultValues.patient}
            {...formProps}
          />
        </Grid>

        <Grid item xs={12} lg={6}>
          <Medicare defaultValues={defaultValues.medicare} {...formProps} />
        </Grid>

        <Grid item xs={12} lg={6}>
          <IhiInformation defaultValues={defaultValues.ihi} {...formProps} />
        </Grid>

        <Grid item xs={12}>
          <AddressForm
            key={`address-form-${residentialAddress?.address || 'empty'}`}
            defaultValues={defaultValues.address}
            {...formProps}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
